import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MESSAGES = [
  { title: "Free shipping", sub: "On orders over $150" },
  { title: "Australian based store", sub: "Fast local delivery" },
  { title: "Hot Black Friday deals", sub: "Use code WELCOME10 — 10% off" },
  { title: "Brand new wellness store", sub: "Curated health & recovery" },
];

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const Hero = () => {
  const [index, setIndex] = useState(0);
  const [slides, setSlides] = useState([]);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then(({ data }) => {
        const imgs = [];
        data.forEach((p) => {
          if (p.image) imgs.push({ src: p.image, name: p.name });
          (p.gallery || []).forEach((g) => imgs.push({ src: g, name: p.name }));
          (p.colors || []).forEach((c) => { if (c.image) imgs.push({ src: c.image, name: p.name }); });
        });
        const unique = imgs.filter((v, i, a) => a.findIndex((x) => x.src === v.src) === i);
        setSlides(shuffle(unique));
      })
      .catch(() => setSlides([{ src: "/images/products/hero.png", name: "Altius" }]));
  }, []);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % MESSAGES.length), 3200);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % slides.length), 4200);
    return () => clearInterval(t);
  }, [slides.length]);

  const msg = MESSAGES[index];
  const current = slides[slide % slides.length] || { src: "/images/products/hero.png", name: "Altius" };

  return (
    <section id="top" data-testid="hero-section" className="relative pt-36 pb-24 lg:pt-44 lg:pb-32 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "url('/images/marble-hero.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.75,
        }}
      />
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-marble/20 via-transparent to-marble/40" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
        >
          <p data-testid="hero-overline" className="text-xs tracking-[0.4em] uppercase text-gold mb-8">
            Premium Wellness & Recovery — Australia
          </p>
          <h1 data-testid="hero-heading" className="font-serif-lux text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-charcoal">
            Recovery,
            <br />
            <em className="text-gold-dark">elevated.</em>
          </h1>
          <p data-testid="hero-copy" className="mt-8 text-base md:text-lg leading-relaxed text-charcoal/60 max-w-md font-light">
            Clinical-grade recovery tools and wellness essentials, curated for those who
            treat rest as seriously as training. Delivered across Australia.
          </p>
          <div className="mt-12 flex flex-wrap items-center gap-6">
            <a
              data-testid="hero-shop-cta"
              href="#shop"
              className="px-10 py-4 bg-gold text-charcoal text-sm tracking-[0.25em] uppercase font-medium rounded-full hover:bg-gold-dark transition-colors duration-300 active:scale-95"
            >
              Shop the Collection
            </a>
            <a
              data-testid="hero-why-cta"
              href="#why"
              className="text-sm tracking-[0.25em] uppercase text-charcoal/70 gold-underline"
            >
              Why Altius
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: "easeOut", delay: 0.2 }}
          className="relative"
        >
          <div className="absolute -inset-4 border border-gold/30 rounded-sm pointer-events-none" />
          <div className="relative w-full aspect-[4/3] overflow-hidden rounded-sm shadow-[0_24px_64px_rgba(212,175,55,0.15)] bg-white">
            <AnimatePresence mode="wait">
              <motion.img
                key={current.src}
                data-testid="hero-image"
                src={current.src}
                alt={current.name}
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.2, ease: "easeInOut" }}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </AnimatePresence>
            <div className="absolute bottom-3 right-3 bg-charcoal/60 backdrop-blur-sm text-marble text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full">
              {current.name}
            </div>
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white/80 backdrop-blur-xl border border-gold/30 px-7 py-5 rounded-sm min-w-[240px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={index}
                data-testid="hero-message"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.4 }}
              >
                <p className="font-serif-lux text-2xl text-charcoal leading-tight">{msg.title}</p>
                <p className="text-xs tracking-widest uppercase text-charcoal/50 mt-1.5">{msg.sub}</p>
              </motion.div>
            </AnimatePresence>
            <div className="flex gap-1.5 mt-3">
              {MESSAGES.map((_, i) => (
                <span
                  key={i}
                  className={`h-1 rounded-full transition-all duration-300 ${i === index ? "w-5 bg-gold" : "w-2 bg-charcoal/20"}`}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      <motion.a
        href="#shop"
        data-testid="hero-scroll-indicator"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-gold hidden lg:block"
      >
        <ArrowDown size={20} strokeWidth={1.5} />
      </motion.a>
    </section>
  );
};

export default Hero;
